import { throttle } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';

import {
	ControlledAutocomplete,
	ControlledAutocompleteProps,
} from './ControlledAutocomplete';

export type AutocompleteAsYouTypeProps<
	T,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<
	ControlledAutocompleteProps<T, TFieldValues, TName>,
	'options' | 'onInputChange' | 'autoComplete' | 'filterOptions'
> & {
	initialInputValue?: string;
	minimumUserInputLength?: number;
	fetchOptions: (input: string, callback: (value: T[]) => void) => void;
};

export function AutocompleteAsYouType<
	T,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	minimumUserInputLength = 3,
	fetchOptions,
	initialInputValue = '',
	...props
}: AutocompleteAsYouTypeProps<T, TFieldValues, TName>): React.JSX.Element {
	const [inputValue, setInputValue] = useState(initialInputValue);
	const [options, setOptions] = useState<T[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	const fetch = useMemo(
		() => throttle(fetchOptions, 1000, { leading: true, trailing: true }),
		[fetchOptions]
	);

	useEffect(() => {
		if (inputValue.length >= minimumUserInputLength) {
			setIsLoading(true);
			fetch(inputValue, (data) => {
				if (data) {
					setOptions(data);
				}
				setIsLoading(false);
			});
		} else {
			setOptions([]);
			setIsLoading(false);
		}

		return () => {
			fetch.cancel();
		};
	}, [inputValue, fetch, minimumUserInputLength]);

	return (
		<ControlledAutocomplete
			autoComplete={false}
			filterOptions={(x) => x}
			options={options}
			onInputChange={(_event, newInputValue) => {
				setInputValue(newInputValue.trim());
			}}
			loading={isLoading}
			{...props}
		/>
	);
}
